import React, { useState } from 'react';
import { FileText, Download, Eye, MessageCircle, AlertCircle, CheckCircle, Search, ChevronDown} from 'lucide-react';
import useSWR from 'swr';
import { createTicket, fetcher, getTicketsUrl, updateTicketStatus } from '../services/ApiSupport';
import { useTranslation } from '../../TranslationContext';
import { useAdminInfo } from '../../admin/pages/Dashboard/DashboarEntreprise/hooksUseretApplication/hookAdmin Info';
import { useNavigate } from 'react-router-dom';

interface Ticket {
  id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  type: string;
  priority: string;
  response?: {
    content: string;
    type: 'text' | 'pdf';
    pdfUrl?: string;
  };
  commentCount?: number;
}

interface TicketResponse {
  isSuccess: boolean;
  data: Ticket[];
  errorMessage: string;
}

interface ModalState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error';
}

interface TicketSystemProps {
  setModal: React.Dispatch<React.SetStateAction<ModalState>>;
}

const TicketSystem: React.FC<TicketSystemProps> = ({ setModal }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'create' | 'view'>('create');
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null); // Track expanded ticket
  const companyId = localStorage.getItem('company') || '';
  const { adminInfo } = useAdminInfo();
  const navigate = useNavigate();

  const { data: ticketsResponse, error, mutate } = useSWR<TicketResponse>(
    companyId ? getTicketsUrl(companyId) : null,
    fetcher,
    {
      revalidateOnFocus: false,
      refreshInterval: 60000,
    }
  );

  const tickets = ticketsResponse?.data || [];

  // const handleSubmitTicket = async (e: React.FormEvent<HTMLFormElement>) => {
  //   e.preventDefault();
  //   const formData = new FormData(e.currentTarget);

  //   const newTicket = {
  //     title: formData.get('subject') as string,
  //     description: formData.get('description') as string,
  //     type: formData.get('category') as string,
  //     priority: formData.get('priority') as string,
  //     createdBy: adminInfo?.email || null,
  //     status: 'open',
  //     companyId: parseInt(companyId) || null,
  //   };

  //   try {
  //     await createTicket(companyId, newTicket);
  //     mutate();
  //     e.currentTarget.reset();
  //     setModal({
  //       isOpen: true,
  //       title: t('ticket_submitted_success'),
  //       message: t('ticket_submitted_success_message'),
  //       type: 'success',
  //     });
  //   } catch (error) {
  //     console.error('Erreur lors de la soumission du ticket:', error);
  //     setModal({
  //       isOpen: true,
  //       title: t('ticket_submission_failed'),
  //       message: t('ticket_submission_failed_message'),
  //       type: 'error',
  //     });
  //   }
  // };
  const handleSubmitTicket = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const form = e.currentTarget;
  
    const newTicket = {
      title: formData.get('subject') as string,
      description: formData.get('description') as string,
      type: formData.get('category') as string,
      priority: formData.get('priority') as string,
      createdBy: adminInfo?.email || null,
      status: 'open',
      companyId: parseInt(companyId) || null,
    };
  
    try {
      await createTicket(companyId, newTicket);
      mutate();
      form.reset(); // Efface le formulaire
      setModal({
        isOpen: true,
        title: t('ticket_submitted_success'),
        message: t('ticket_submitted_success_message'),
        type: 'success',
      });
    } catch (error) {
      console.error('Erreur lors de la soumission du ticket:', error);
      setModal({
        isOpen: true,
        title: t('ticket_submission_failed'),
        message: t('ticket_submission_failed_message'),
        type: 'error',
      });
    }
  };
  console.log('id et ticket',tickets)
  console.log('id et ticket',tickets)
  const filteredTickets = tickets.filter((ticket: Ticket) => {
    if (activeFilter === 'all') return true;
    return ticket.status.toLowerCase() === activeFilter;
  });

  const handleCloseTicket = async (ticketId: string) => {
    try {
      await updateTicketStatus(companyId, ticketId, 'Closed');
      mutate();
      setExpandedTicket(null); // Collapse the ticket
      setModal({
        isOpen: true,
        title: t('ticket_closed_success'),
        message: t('ticket_closed_success_message'),
        type: 'success',
      });
    } catch (error) {
      console.error('Erreur lors de la fermeture du ticket:', error);
      setModal({
        isOpen: true,
        title: t('ticket_close_failed'),
        message: t('ticket_close_failed_message'),
        type: 'error',
      });
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  const viewTicketDetails = (ticketId: string) => {
    navigate(`/support/ticket/${ticketId}`);
  };

  const toggleTicket = (ticketId: string) => {
    setExpandedTicket(expandedTicket === ticketId ? null : ticketId);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-slate-800 mb-4 sm:mb-0">
          {t('support_tickets')}
        </h2>
        <div className="flex bg-white rounded-lg shadow-sm p-1 border border-slate-200">
          <button
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              activeTab === 'create'
                ? 'bg-sky-500 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
            }`}
            onClick={() => setActiveTab('create')}
          >
            {t('create_ticket')}
          </button>
          <button
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
              activeTab === 'view'
                ? 'bg-sky-500 text-white shadow-md'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
            }`}
            onClick={() => setActiveTab('view')}
          >
            {t('my_tickets')}
          </button>
        </div>
      </div>

      {activeTab === 'create' ? (
        <div className="bg-white rounded-xl shadow-md p-8 border border-slate-200">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mr-4">
              <MessageCircle className="w-5 h-5 text-sky-500" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-800">
              {t('create_new_ticket')}
            </h3>
          </div>

          <div className="bg-slate-50 rounded-lg p-6 mb-8 border-l-4 border-sky-400">
            <h4 className="text-lg font-medium text-slate-800 mb-3 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-sky-500" />
              {t('tips_for_resolution')}
            </h4>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('tip_describe_problem')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('tip_include_screenshots')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{t('tip_list_steps')}</span>
              </li>
            </ul>
          </div>

          <form onSubmit={handleSubmitTicket} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('subject')}
              </label>
              <input
                type="text"
                name="subject"
                required
                placeholder={t('subject_placeholder')}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('detailed_description')}
              </label>
              <textarea
                name="description"
                required
                rows={6}
                placeholder={t('description_placeholder')}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors duration-200"
              ></textarea>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('priority')}
                </label>
                <select
                  name="priority"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors duration-200"
                >
                  <option value="low">{t('priority_low')}</option>
                  <option value="medium">{t('priority_medium')}</option>
                  <option value="high">{t('priority_high')}</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('category')}
                </label>
                <select
                  name="category"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors duration-200"
                >
                  <option value="technical">{t('category_technical')}</option>
                  <option value="billing">{t('category_billing')}</option>
                  <option value="feature">{t('category_feature')}</option>
                  <option value="other">{t('category_other')}</option>
                </select>
              </div>
            </div>
            <div className="pt-4">
              <button
                type="submit"
                className="w-full bg-sky-500 hover:bg-sky-600 text-white py-3 px-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 shadow-lg shadow-sky-500/20 flex items-center justify-center"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {t('submit_ticket')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div>
          <div className="bg-white rounded-xl shadow-md p-8 border border-slate-200">
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  activeFilter === 'all'
                    ? 'bg-sky-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                onClick={() => setActiveFilter('all')}
              >
                {t('all_tickets')}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  activeFilter === 'open'
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                onClick={() => setActiveFilter('open')}
              >
                {t('open_tickets')}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  activeFilter === 'closed'
                    ? 'bg-slate-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                onClick={() => setActiveFilter('closed')}
              >
                {t('resolved_tickets')}
              </button>
            </div>

            <div className="space-y-4">
              {error ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-rose-500" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">{t('error_loading_tickets')}</h3>
                  <p className="text-slate-500">{t('error_loading_tickets_message')}</p>
                </div>
              ) : !ticketsResponse ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">{t('loading_tickets')}</h3>
                  <p className="text-slate-500">{t('loading_tickets_message')}</p>
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">{t('no_tickets_found')}</h3>
                  <p className="text-slate-500">{t('no_tickets_message')}</p>
                </div>
              ) : (
                filteredTickets.map((ticket: Ticket) => (
                  <div
                    key={ticket.id}
                    className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow duration-200"
                  >
                    <div
                      className="flex justify-between items-center p-6 cursor-pointer"
                      onClick={() => toggleTicket(ticket.id)}
                    >
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium text-slate-800 mr-3">{ticket.title}</h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            ticket.status?.toLowerCase() === 'open'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          {ticket.status?.toLowerCase() === 'open' ? t('status_open') : t('status_resolved')}
                        </span>
                      </div>
                      <ChevronDown
                        className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${
                          expandedTicket === ticket.id ? 'rotate-180' : ''
                        }`}
                      />
                    </div>

                    {expandedTicket === ticket.id && (
                      <div className="p-6 pt-0 transition-all duration-300 ease-in-out">
                        <div className="border-t border-slate-200 pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <span className="text-sm font-medium text-slate-700">{t('ticket_number')}:</span>
                              <span className="text-sm text-slate-600 ml-1">#{ticket.id}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-slate-700">{t('created_on')}:</span>
                              <span className="text-sm text-slate-600 ml-1">{formatDate(ticket.createdAt)}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-slate-700">{t('priority')}:</span>
                              <span className="text-sm text-slate-600 ml-1 capitalize">{ticket.priority}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-slate-700">{t('category')}:</span>
                              <span className="text-sm text-slate-600 ml-1 capitalize">{ticket.type}</span>
                            </div>
                          </div>

                          <div className="bg-slate-50 rounded-lg p-4 mb-4">
                            <h4 className="text-sm font-medium text-slate-700 mb-2">{t('description')}:</h4>
                            <p className="text-slate-600">{ticket.description}</p>
                          </div>

                          {ticket.response && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center">
                                <MessageCircle className="w-4 h-4 mr-2 text-sky-500" />
                                {t('support_response')}
                              </h4>
                              {ticket.response.type === 'text' ? (
                                <div className="bg-sky-50 border-l-4 border-sky-400 p-4 rounded-r-lg">
                                  <p className="text-slate-600">{ticket.response.content}</p>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200">
                                  <div className="flex items-center">
                                    <FileText className="h-8 w-8 text-slate-400 mr-3" />
                                    <div>
                                      <span className="block text-slate-800 font-medium">
                                        {ticket.response.content}
                                      </span>
                                      <span className="text-xs text-slate-500">{t('pdf_document')}</span>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      className="inline-flex items-center px-3 py-1 bg-sky-100 text-sky-600 rounded-md text-sm hover:bg-sky-200 transition-colors duration-200"
                                      onClick={() => window.open(ticket.response?.pdfUrl, '_blank')}
                                    >
                                      <Eye className="h-4 w-4 mr-1" />
                                      {t('view')}
                                    </button>
                                    <button
                                      className="inline-flex items-center px-3 py-1 bg-sky-500 text-white rounded-md text-sm hover:bg-sky-600 transition-colors duration-200"
                                      onClick={() => window.open(ticket.response?.pdfUrl, '_blank')}
                                    >
                                      <Download className="h-4 w-4 mr-1" />
                                      {t('download')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {ticket.commentCount && ticket.commentCount > 0 && (
                            <div className="flex items-center text-sm text-slate-500 mb-4">
                              <MessageCircle className="w-4 h-4 mr-1 text-sky-500" />
                              {ticket.commentCount} {ticket.commentCount === 1 ? t('comment') : t('comments')}
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <button
                              className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded-md hover:bg-slate-200 transition-colors duration-200"
                              onClick={() => viewTicketDetails(ticket.id)}
                            >
                              {t('view_details')}
                            </button>
                            {ticket.status?.toLowerCase() === 'open' && (
                              <button
                                className="px-3 py-1 text-xs bg-rose-100 text-rose-600 rounded-md hover:bg-rose-200 transition-colors duration-200"
                                onClick={() => handleCloseTicket(ticket.id)}
                              >
                                {t('close_ticket')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketSystem;
